import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../context/SessionContext';

type Version = {
  id: string;
  versionNumber: number;
  label?: string | null;
  createdAt: string;
  metadata?: Record<string, unknown>;
};

export function RefinePage() {
  const { assetId = '' } = useParams();
  const { authenticated, startKeyra } = useSession();
  const [versions, setVersions] = useState<Version[]>([]);
  const [kind, setKind] = useState<'material' | 'geometry' | 'prompt'>('material');
  const [instructions, setInstructions] = useState('Keep the shape but change the finish to brushed brass.');
  const [locked, setLocked] = useState('base,stem');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function load() {
    const data = await api.listVersions(assetId);
    setVersions(data.versions);
  }

  useEffect(() => {
    if (!authenticated) {
      void startKeyra(`/refine/${assetId}`);
      return;
    }
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Failed'));
  }, [authenticated, assetId, startKeyra]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    try {
      const result = await api.refineAsset(assetId, {
        kind,
        instructions,
        lockedComponentIds: locked
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setInfo(
        kind === 'material'
          ? `Material revision ${result.version?.versionNumber ?? ''} created — geometry preserved.`
          : `Revision created (${result.jobId ?? result.version?.id ?? 'ok'})`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refine failed');
    }
  }

  if (!authenticated) {
    return (
      <main className="page">
        <p className="muted">Sign in to refine assets.</p>
      </main>
    );
  }

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)' }}>Refine</h1>
      <p className="muted">
        Structured edits against stable component IDs. Material-only changes must not regenerate geometry.
        Failed iterations never overwrite a successful version.
      </p>
      <form className="panel" style={{ maxWidth: 640, marginTop: '1rem' }} onSubmit={onSubmit}>
        <label className="field">
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="material">Material only</option>
            <option value="geometry">Geometry</option>
            <option value="prompt">Prompt iteration</option>
          </select>
        </label>
        <label className="field" style={{ display: 'block', marginTop: '0.75rem' }}>
          Instructions
          <textarea className="prompt" style={{ minHeight: 100 }} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </label>
        <label className="field" style={{ display: 'block', marginTop: '0.75rem' }}>
          Locked component IDs (comma-separated)
          <input value={locked} onChange={(e) => setLocked(e.target.value)} />
        </label>
        <button className="btn primary" type="submit" style={{ marginTop: '0.85rem' }}>
          Commit revision
        </button>
      </form>
      {info && <p className="ok">{info}</p>}
      {error && <p className="err">{error}</p>}
      <div className="panel" style={{ marginTop: '1rem' }}>
        <h2>Version history</h2>
        <ul>
          {versions.map((v) => (
            <li key={v.id} className="row" style={{ justifyContent: 'space-between', marginBottom: '0.35rem' }}>
              <span>
                v{v.versionNumber} {v.label ?? ''} <span className="muted mono">{v.createdAt}</span>
              </span>
              <button
                type="button"
                className="btn"
                onClick={() => void api.restoreVersion(assetId, v.id).then(load)}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
        <div className="row">
          <Link className="btn" to={`/spin/${assetId}`}>
            Spin it
          </Link>
          <Link className="btn" to={`/publish/${assetId}`}>
            Publish
          </Link>
          <button type="button" className="btn" onClick={() => void api.duplicateAsset(assetId).then(() => setInfo('Duplicated as variation'))}>
            Duplicate as variation
          </button>
        </div>
      </div>
    </main>
  );
}
